import { useLocalStorage } from 'usehooks-ts';
import { useRouter } from 'next/router';
import { LinkWrapperProps, NavSection, UiKit } from '@datavant/uikit';
import { useSideNav } from '../lib/buildSideNav';
import React, { useContext } from 'react';
import useUser from '../lib/useUser';
import { UserWithPermissions } from '../lib/user';
import { UserTypeContext } from '../lib/contexts/UserTypeContext';
import { useUserPermissions } from '../lib/contexts/PermissionsProviderContext';
import { Permission } from '../lib/permissions';
import { TenantType } from '../lib/user/enums';
import { DebugFlag } from '../lib/user/debugFlags';
import Link from 'next/link';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { RRAOnboardingTracker } from './RequestManager/Autofulfill/EMRConfigs/RRAOnboardingTracker';
import { NewButtonMenu } from './NewButtonMenu';
import { ProviderExchangeCard } from './ProviderExchangeCard';

const NextLink: React.FC<LinkWrapperProps> = (props: LinkWrapperProps): JSX.Element => {
    return (
        <Link
            href={{
                pathname: props.path,
            }}
            passHref={true}
            legacyBehavior={true}
        >
            <a>{props.children}</a>
        </Link>
    );
};

const useDatavanterLinks = (user: UserWithPermissions) => {
    const featureFlags = useFlags();
    const {
        userPermissions: {
            CAN_VIEW_DATAVANTER_MENU: canViewDatavanterMenu,
            CAN_CREATE_TENANTS: canCreateTenants,
            CAN_VIEW_AUDIT_LOGS: canViewAuditLogs,
            CAN_VIEW_DLQ_TRIAGE: canViewDlqTriage,
        },
    } = useUserPermissions([
        Permission.CAN_VIEW_DATAVANTER_MENU,
        Permission.CAN_CREATE_TENANTS,
        Permission.CAN_VIEW_AUDIT_LOGS,
        Permission.CAN_VIEW_DLQ_TRIAGE,
    ]);

    if (!user || !canViewDatavanterMenu) {
        return undefined;
    }

    const datavanterLinks = [
        {
            label: 'QA Admin',
            path: '/datavanter/qa-admin',
        },
        {
            label: 'UiKit Gallery',
            path: '/datavanter/ui',
        },
        {
            label: 'LaunchDarkly Flags',
            path: '/datavanter/test/LDFlags',
        },
        {
            label: 'Data Management',
            path: '/datavanter/data-management',
        },
        {
            label: 'Implementation Toolkit',
            path: '/datavanter/implementation-toolkit',
        },
        {
            label: 'Health Systems',
            path: '/datavanter/health-systems',
        },
        {
            label: 'Analytics',
            path: '/datavanter/analytics',
        },
        {
            label: 'Request Manager Internal',
            path: '/datavanter/request-manager-internal',
        },
        {
            label: 'API Testing',
            path: '/datavanter/test/api',
        },
        {
            label: 'Admin Tools',
            path: '/datavanter/admin',
        },
        {
            label: 'Autofulfill Tracker',
            path: '/datavanter/autofulfill-tracker',
        },
        {
            label: 'Payer Semi Auto Member Locator',
            path: '/datavanter/payer/semi-auto-member-locator',
        },
        {
            label: 'Re-Upload Start File',
            path: '/datavanter/re-upload-start-file',
        },
    ];

    if (canCreateTenants) {
        const tenantManagement = {
            label: 'Tenant Management',
            path: '/datavanter/tenant',
        };
        // Insert at its specific index.
        datavanterLinks.splice(3, 0, tenantManagement);
    }

    if (canViewAuditLogs) {
        const auditLogs = {
            label: 'Audit Logs',
            path: '/datavanter/audit-logs',
        };
        datavanterLinks.splice(0, 0, auditLogs);
    }

    // Engineer-only DLQ triage tooling, gated on the RBAC permission (plus the
    // isTrueDatavanter check enforced by the page shell and the tRPC procedures).
    if (canViewDlqTriage) {
        // A single entry point to the triage dashboard; the Explorer and SQL Console are
        // reachable from within it, so we no longer surface them as separate nav links.
        datavanterLinks.push({
            label: 'DLQ Triage',
            path: '/datavanter/dlq-triage',
        });
    }

    return datavanterLinks;
};

export const SideNav = (): JSX.Element => {
    const [isOpen, setIsOpen] = useLocalStorage('idsb-sidebar-open', true);
    const featureFlags = useFlags();

    const router = useRouter();
    // @ts-expect-error - TS2322 - Type 'UserWithPermissions | undefined' is not assignable to type 'UserWithPermissions'.
    const currentUser: UserWithPermissions = useUser().user;

    const debugUserOverride = useContext(UserTypeContext);
    const debugFlags = {
        isTpo: debugUserOverride.value.user === TenantType.TPO,
    } as DebugFlag;
    const sections: NavSection[] = useSideNav(router, currentUser, debugFlags, featureFlags);
    const datavanterLinks = useDatavanterLinks(currentUser);

    return (
        <UiKit.Nav
            topAdditionalContent={<NewButtonMenu />}
            linkWrapperComponent={NextLink}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            navTop={{
                sections: sections,
            }}
            middleAdditionalContent={
                <>
                    {currentUser?.isProviderUser && <ProviderExchangeCard isSideNavOpen={isOpen} />}
                    {featureFlags['canAccessAutofulfillOnboardingTracker'] && currentUser?.isProviderUser && (
                        <RRAOnboardingTracker isSideNavOpen={isOpen} />
                    )}
                </>
            }
            navBottom={{
                datavanterLinks,
            }}
        />
    );
};
